import { useCallback, useEffect, useRef } from 'react';

/**
 * useDraft — generic, HIPAA-safe "unsaved work" persistence.
 *
 * Continuously (debounced) snapshots a surface's unsaved form state to
 * sessionStorage so it can be restored after an involuntary re-login (the
 * 30-minute HIPAA idle timeout hard-redirects to /login → Google OAuth →
 * back into the app). This rides the same seam as `returnTo`: neither
 * `logout()` nor `forceLogout()` calls `sessionStorage.clear()` (they remove
 * only specific keys), so a namespaced draft survives the timeout→redirect.
 *
 * HIPAA: sessionStorage ONLY, never localStorage — allocation amounts and note
 * text can carry payer/patient context, so drafts must clear on tab close
 * (identical posture to `authToken` and `returnTo`). A 24h TTL + explicit
 * clear-on-save/-discard + purge-on-voluntary-logout bound the exposure.
 *
 * The hook is intentionally type-generic: each surface owns its own
 * (serialize ← live state) / (hydrate → setState) mapping; this layer only
 * handles the storage envelope, debounce, TTL, and record-scoped isolation.
 */

// Greppable, minification-proof marker so a deploy can verify this feature
// shipped by grepping the served bundle (mirrors returnTo's RETURN_TO_KEY).
export const DRAFT_KEY_PREFIX = 'agi.draft.';
export const DRAFT_SCHEMA_VERSION = 1;
// Matches returnTo's MAX_AGE_MS — a draft older than the return-to window is
// stale intent and is dropped (and purged) on read.
export const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

export interface DraftEnvelope<T> {
  v: number;
  surface: string;
  recordId: string;
  ts: number;
  data: T;
}

// key = `agi.draft.<surface>.<recordId>`
export const draftKey = (surface: string, recordId: string): string =>
  `${DRAFT_KEY_PREFIX}${surface}.${recordId}`;

/**
 * Read + validate a draft. Returns the parsed `data` only when the envelope is
 * the current schema version, matches `surface`/`recordId`, and is < TTL;
 * otherwise removes the (stale/mismatched/corrupt) key and returns null.
 */
export function readDraft<T>(surface: string, recordId: string): T | null {
  const key = draftKey(surface, recordId);
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;

    const env = JSON.parse(raw) as Partial<DraftEnvelope<T>>;
    const fresh =
      env.v === DRAFT_SCHEMA_VERSION &&
      env.surface === surface &&
      env.recordId === recordId &&
      typeof env.ts === 'number' &&
      Date.now() - env.ts <= DRAFT_TTL_MS &&
      env.data !== undefined;

    if (!fresh) {
      sessionStorage.removeItem(key);
      return null;
    }
    return env.data as T;
  } catch {
    // Corrupt entry — purge it and treat as no draft.
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* storage unavailable */
    }
    return null;
  }
}

/** Write a draft envelope. Best-effort — storage failures are swallowed. */
export function writeDraft<T>(surface: string, recordId: string, data: T): void {
  const envelope: DraftEnvelope<T> = {
    v: DRAFT_SCHEMA_VERSION,
    surface,
    recordId,
    ts: Date.now(),
    data,
  };
  try {
    sessionStorage.setItem(draftKey(surface, recordId), JSON.stringify(envelope));
  } catch {
    // Quota / storage unavailable — persistence is best-effort.
  }
}

/** Remove one draft (used on save / discard / drop-on-reopen). */
export function removeDraft(surface: string, recordId: string): void {
  try {
    sessionStorage.removeItem(draftKey(surface, recordId));
  } catch {
    /* storage unavailable */
  }
}

/**
 * Purge EVERY draft key. Call on a *voluntary* logout only — an involuntary
 * (idle) logout must PRESERVE drafts (that is the whole feature).
 */
export function purgeAllDrafts(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(DRAFT_KEY_PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* storage unavailable */
  }
}

interface UseDraftOptions {
  debounceMs?: number;
}

export interface UseDraftApi<T> {
  /** Fresh (< TTL) parsed data for this record, else null (purges stale). */
  loadDraft: () => T | null;
  /** Debounced write of the current snapshot. */
  saveDraft: (data: T) => void;
  /** Cancel any pending write and remove this record's draft key. */
  clearDraft: () => void;
}

export function useDraft<T>(
  surface: string,
  recordId: string | null,
  opts: UseDraftOptions = {},
): UseDraftApi<T> {
  const { debounceMs = 500 } = opts;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Holds the not-yet-written snapshot (with the recordId captured at call
  // time) so it can be flushed on unmount / record switch.
  const pendingRef = useRef<{ recordId: string; data: T } | null>(null);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingRef.current;
    if (pending) {
      writeDraft(surface, pending.recordId, pending.data);
      pendingRef.current = null;
    }
  }, [surface]);

  const loadDraft = useCallback((): T | null => {
    if (!recordId) return null;
    return readDraft<T>(surface, recordId);
  }, [surface, recordId]);

  const saveDraft = useCallback(
    (data: T): void => {
      if (!recordId) return;
      pendingRef.current = { recordId, data };
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        const pending = pendingRef.current;
        if (pending) {
          writeDraft(surface, pending.recordId, pending.data);
          pendingRef.current = null;
        }
      }, debounceMs);
    },
    [surface, recordId, debounceMs],
  );

  const clearDraft = useCallback((): void => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    // Null the pending snapshot so a subsequent unmount-flush can never
    // resurrect a draft the caller just cleared (post-save / post-discard).
    pendingRef.current = null;
    if (!recordId) return;
    removeDraft(surface, recordId);
  }, [surface, recordId]);

  // Flush a pending debounced write when the record switches or the component
  // unmounts, so a logout→redirect immediately after the last keystroke still
  // persists the final snapshot. The pending snapshot carries its own recordId,
  // so switching deposits flushes the PREVIOUS record's draft correctly.
  useEffect(() => {
    return () => {
      flush();
    };
  }, [recordId, flush]);

  return { loadDraft, saveDraft, clearDraft };
}

export default useDraft;
